"use client";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
export default function Comparison() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const tiers = [{
    tag: "STARTER",
    price: "$450k",
    unit: "· starting price",
    featured: false,
    inclusions: ["Essential floor plans", "Standard energy efficiency", "Core material selection", "Basic warranty coverage", "Standard build timeline"]
  }, {
    tag: "PREMIER",
    price: "$650k",
    unit: "· starting price",
    featured: true,
    inclusions: ["Custom architectural design", "High-efficiency systems", "Premium finish packages", "Extended structural warranty", "Priority project management"]
  }, {
    tag: "LUXURY",
    price: "$900k+",
    unit: "· starting price",
    featured: false,
    inclusions: ["Bespoke estate planning", "Smart home integration", "Designer curated interiors", "Lifetime structural guarantee", "Dedicated concierge service"]
  }];
  return <section data-section-id="1631" id="comparison-plans" className="relative bg-background text-foreground py-32 md:py-44 overflow-hidden font-default">
      {}
      <div aria-hidden="true" className="pointer-events-none select-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-[40%] font-sans italic font-light text-foreground/[0.04] leading-none z-0" style={{
      fontSize: "clamp(12rem, 22vw, 21rem)"
    }}>
        BUILD
      </div>

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-16">
        {}
        <div ref={headerRef} className={`grid grid-cols-12 gap-6 md:gap-8 mb-20 md:mb-28 transition-all duration-[900ms] ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          {}
          <div className="col-span-12 md:col-span-4">
            <div className="flex items-center gap-4 mb-6">
              <span className="font-sans uppercase text-primary leading-none" style={{
              fontSize: "clamp(0.6875rem, 0.78vw, 0.8125rem)",
              letterSpacing: "0.22em"
            }}>
                § BUILDER COMPARISON — OHIO MARKET
              </span>
            </div>
            <div aria-hidden="true" className="h-px w-60 mb-8" style={{
            background: "linear-gradient(to right, var(--primary) 0%, transparent 100%)"
          }} />
            <h2 className="font-sans font-bold text-foreground leading-[1.02]" style={{
            fontSize: "clamp(2.25rem, 5vw, 4.5rem)",
            letterSpacing: "-0.025em"
          }}>
              Find your{" "}
              <span className="block">
                perfect{" "}
                <em className={`italic text-primary transition-opacity duration-500 ${headerVisible ? "opacity-100" : "opacity-0"}`} style={{
                transitionDelay: "200ms"
              }}>
                  home builder
                </em>
                .
              </span>
            </h2>
          </div>

          {}
          <div className="col-span-12 md:col-span-8 md:pl-16 flex flex-col justify-end">
            <p className="text-foreground/90 font-default font-normal max-w-2xl" style={{
            fontSize: "clamp(1.0625rem, 1.25vw, 1.25rem)",
            lineHeight: "1.65"
          }}>
              BuildIQ provides transparent comparisons of central Ohio's top home builders. 
              We eliminate the guesswork by analyzing pricing, quality, and service 
              standards so you can build with confidence.
            </p>
            <div className="mt-8 flex items-center gap-3">
              <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-lg bg-primary" />
              <span className="font-sans uppercase text-muted-foreground" style={{
              fontSize: "0.75rem",
              letterSpacing: "0.18em"
            }}>
                TRANSPARENT PRICING · VERIFIED BUILDERS
              </span>
            </div>
          </div>
        </div>

        {}
        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {tiers.map((tier, i) => {
          const isFeatured = tier.featured;
          return <div key={i} data-index={i} className={`group relative bg-card border transition-all duration-[320ms] ease-out p-10 flex flex-col min-h-[480px] rounded-lg ${isFeatured ? "border-primary md:-translate-y-2 order-first md:order-none" : "border-border hover:border-primary"} ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: cardsVisible ? `${i * 80}ms` : "0ms",
            transitionDuration: "900ms"
          }}>
                {}
                <span aria-hidden="true" className={`absolute top-3 left-3 w-3 h-3 transition-opacity duration-[220ms] ${isFeatured ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`} style={{
              transitionDelay: isFeatured ? "0ms" : "220ms",
              borderTop: "1px solid var(--primary)",
              borderLeft: "1px solid var(--primary)"
            }} />
                {}
                <span aria-hidden="true" className={`absolute bottom-3 right-3 w-3 h-3 transition-opacity duration-[220ms] ${isFeatured ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`} style={{
              transitionDelay: isFeatured ? "0ms" : "220ms",
              borderBottom: "1px solid var(--primary)",
              borderRight: "1px solid var(--primary)"
            }} />

                {}
                {isFeatured && <Badge variant="outline" className="absolute top-6 right-6 bg-transparent border-primary text-primary font-sans uppercase rounded-lg px-2.5 py-1" style={{
              fontSize: "0.6875rem",
              letterSpacing: "0.18em"
            }}>
                    MOST POPULAR
                  </Badge>}

                {}
                <div className="mb-4">
                  <Badge variant="outline" className="bg-transparent border-border text-foreground font-sans uppercase rounded-lg px-2.5 py-1" style={{
                fontSize: "0.6875rem",
                letterSpacing: "0.18em"
              }}>
                    {tier.tag}
                  </Badge>
                </div>

                {}
                <div aria-hidden="true" className="h-px w-full mb-8" style={{
              background: "linear-gradient(to right, var(--primary) 0%, transparent 240px)"
            }} />

                {}
                <div className="mb-1">
                  <span className="font-sans font-bold text-foreground leading-none" style={{
                fontSize: "clamp(3rem, 5vw, 4.5rem)",
                letterSpacing: "-0.02em"
              }}>
                    {tier.price}
                  </span>
                </div>
                <div className="mb-8">
                  <span className="font-sans text-muted-foreground uppercase" style={{
                fontSize: "0.75rem",
                letterSpacing: "0.14em"
              }}>
                    {tier.unit}
                  </span>
                </div>

                {}
                <ul className="flex flex-col gap-3 mb-10 flex-grow">
                  {tier.inclusions.map((item, idx) => <li key={idx} data-index={idx} className="flex items-start gap-3 text-foreground/90">
                      <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-lg bg-primary mt-2 shrink-0" />
                      <span className="font-default" style={{
                  fontSize: "0.9375rem",
                  lineHeight: "1.6"
                }}>
                        {item}
                      </span>
                    </li>)}
                </ul>

                {}
                <Link to="#" className="block w-full">
                  <Button className="w-full bg-primary text-primary-foreground border border-primary hover:bg-primary/90 hover:border-primary/90 rounded-lg font-sans uppercase py-[18px] h-auto transition-colors duration-[220ms]" style={{
                fontSize: "0.75rem",
                letterSpacing: "0.2em"
              }}>
                    GET QUOTE
                  </Button>
                </Link>
              </div>;
        })}
        </div>
      </div>
    </section>;
}